import * as Sentry from '@sentry/react';

export { Sentry };

const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const REDACTED_EXTRA_RE = /submission.content|pitch.deck|document.text|body_html/i;

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: each optional Sentry payload collection needs independent PII scrubbing
function scrubPii(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  if (event.user?.email) {
    delete event.user.email;
  }
  if (event.exception?.values) {
    for (const ex of event.exception.values) {
      if (ex.value) ex.value = ex.value.replace(EMAIL_RE, '[email]');
    }
  }
  if (event.breadcrumbs) {
    for (const bc of event.breadcrumbs) {
      if (bc.message) bc.message = bc.message.replace(EMAIL_RE, '[email]');
    }
  }
  if (event.extra) {
    for (const key of Object.keys(event.extra)) {
      if (REDACTED_EXTRA_RE.test(key)) event.extra[key] = '[redacted]';
    }
  }
  return event;
}

export const initSentry = () => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const isDevelopment = import.meta.env.DEV;
  const environment = import.meta.env.VITE_APP_ENV || (isDevelopment ? 'development' : 'production');

  const includeReplay = environment !== 'development' && environment !== 'lab';

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    tracesSampleRate: isDevelopment ? 0.5 : 0.1,
    sampleRate: 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: includeReplay ? 0.5 : 0,
    sendDefaultPii: false,
    maxBreadcrumbs: isDevelopment ? 50 : 30,
    integrations: [
      Sentry.browserTracingIntegration({
        enableLongTask: true,
        enableInp: true,
      }),
      ...(includeReplay ? [Sentry.replayIntegration()] : []),
    ],
    ignoreErrors: ['ResizeObserver loop limit exceeded', 'ChunkLoadError'],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i],
    beforeSend(event) {
      if (document.visibilityState === 'hidden') {
        const msg = event.exception?.values?.[0]?.value ?? '';
        if (/NavigatorOffline/i.test(msg)) {
          return null;
        }
      }
      return scrubPii(event);
    },
  });

  if (environment === 'development' || environment === 'lab') {
    (window as unknown as Record<string, unknown>).__SENTRY_SELF_TEST__ = () => {
      Sentry.captureMessage('Sentry self-test', {
        level: 'info',
        tags: { self_test: 'true', environment },
      });
      // biome-ignore lint/suspicious/noConsole: intentional dev/lab diagnostic
      console.info('[sentry] Self-test event sent — check Bugsink for arrival');
    };
  }
};
